/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type PointerEvent as ReactPointerEvent, useCallback, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';

/** Draw-to-sign canvas. Emits a PNG data URL on "Save signature". */
export function SignaturePad({
  onCapture,
  saving,
}: {
  readonly onCapture: (dataUrl: string) => void;
  readonly saving: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  const point = useCallback((e: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (canvas === null) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const onDown = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx === null || ctx === undefined) return;
      drawing.current = true;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#0f172a';
      const { x, y } = point(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    [point],
  );

  const onMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!drawing.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx === null || ctx === undefined) return;
      const { x, y } = point(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    },
    [point],
  );

  const onUp = useCallback(() => {
    drawing.current = false;
  }, []);

  const onClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas === null || ctx === null || ctx === undefined) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const onSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    onCapture(canvas.toDataURL('image/png'));
  }, [onCapture]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={420}
        height={140}
        aria-label={t('intake.sign.canvasLabel')}
        className="w-full max-w-md touch-none rounded-xl border border-slate-300 bg-white dark:border-slate-600"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      />
      <div className="mt-3 flex gap-3">
        <Button type="button" variant="outline" onClick={onClear}>
          {t('intake.sign.clear')}
        </Button>
        <Button type="button" onClick={onSave} disabled={saving}>
          {t('intake.sign.save')}
        </Button>
      </div>
    </div>
  );
}
